import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Loader2, X, Keyboard } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage, api } from '../context/AppContext';

export default function GeolocationPrompt({ onComplete }) {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [visible, setVisible] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [showPostal, setShowPostal] = useState(false);
  const [postalCode, setPostalCode] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if geo already saved
    api.get('/geolocation/profile').then(res => {
      if (!res.data.geo_profile) {
        setTimeout(() => setVisible(true), 2000);
      }
    }).catch(() => {});
  }, []);

  const detectLocation = useCallback(async () => {
    setDetecting(true);
    setError(null);

    if (!navigator.geolocation) {
      setError(fr ? 'Geolocalisation non supportee' : 'Geolocation not supported');
      setDetecting(false);
      setShowPostal(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          // Reverse geocode with free API
          const geoRes = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=fr`,
            { headers: { 'User-Agent': 'YUMIX-ExtraMiles/1.0' } }
          );
          const geoData = await geoRes.json();
          const city = geoData.address?.city || geoData.address?.town || geoData.address?.village || '';
          const pc = geoData.address?.postcode || '';
          const province = geoData.address?.state || 'Quebec';

          const saveRes = await api.post('/geolocation/save', {
            latitude, longitude, city, postal_code: pc, province
          });

          if (saveRes.data.success) {
            setResult({
              region: saveRes.data.region,
              stores: saveRes.data.stores,
              city
            });
          }
        } catch (err) {
          console.error('Geocode error:', err);
          setError(fr ? 'Erreur de detection. Entre ton code postal.' : 'Detection error. Enter your postal code.');
          setShowPostal(true);
        }
        setDetecting(false);
      },
      (err) => {
        console.error('Geolocation error:', err);
        setDetecting(false);
        setShowPostal(true);
        if (err.code === 1) {
          setError(fr ? 'Permission refusée. Entre ton code postal.' : 'Permission denied. Enter your postal code.');
        } else {
          setError(fr ? 'Position introuvable. Entre ton code postal.' : 'Position unavailable. Enter your postal code.');
        }
      },
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }
    );
  }, [fr]);

  const handlePostalSubmit = async () => {
    const pc = postalCode.trim().toUpperCase();
    if (pc.length < 3) return;
    try {
      const res = await api.post('/geolocation/save', { postal_code: pc });
      if (res.data.success) {
        setResult({ region: res.data.region, stores: res.data.stores });
      }
    } catch { }
  };

  const handleDismiss = () => {
    setVisible(false);
    if (onComplete) onComplete(result);
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="fixed bottom-24 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:w-96 z-40"
        data-testid="geolocation-prompt"
      >
        <div className="bg-card border border-border rounded-2xl shadow-2xl p-5 relative">
          <button onClick={handleDismiss} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>

          {result ? (
            <div className="text-center" data-testid="geo-result">
              <div className="w-12 h-12 bg-emerald-500/15 rounded-full flex items-center justify-center mx-auto mb-3">
                <MapPin className="w-6 h-6 text-emerald-500" />
              </div>
              <p className="font-bold text-sm mb-1">{result.region}</p>
              <p className="text-xs text-muted-foreground mb-3">
                {result.stores.length} {fr ? 'magasins disponibles' : 'stores available'}
              </p>
              <div className="flex flex-wrap gap-1.5 justify-center mb-4">
                {result.stores.slice(0, 8).map(s => (
                  <span key={s} className="text-[11px] bg-secondary px-2 py-1 rounded-md font-medium">{s}</span>
                ))}
              </div>
              <Button onClick={handleDismiss} size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white w-full">
                {fr ? 'Parfait!' : 'Perfect!'}
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-500/15 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <p className="font-bold text-sm">
                    {fr ? 'Ou fais-tu ton epicerie?' : 'Where do you shop?'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {fr
                      ? 'Pour te recommander les bons magasins pres de chez toi'
                      : 'To recommend the right stores near you'}
                  </p>
                </div>
              </div>

              {error && (
                <p className="text-xs text-destructive mb-3 px-1">{error}</p>
              )}

              <div className="space-y-2">
                <Button
                  onClick={detectLocation}
                  disabled={detecting}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="geo-detect-btn"
                >
                  {detecting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <MapPin className="w-4 h-4 mr-2" />
                  )}
                  {detecting
                    ? (fr ? 'Detection...' : 'Detecting...')
                    : (fr ? 'Detecter ma position' : 'Detect my location')}
                </Button>

                {showPostal ? (
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value.toUpperCase().slice(0, 7))}
                      onKeyDown={(e) => e.key === 'Enter' && handlePostalSubmit()}
                      placeholder="H2X 1Y4"
                      className="flex-1 font-mono text-sm"
                      data-testid="geo-postal-input"
                    />
                    <Button onClick={handlePostalSubmit} variant="outline" data-testid="geo-postal-submit">
                      OK
                    </Button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowPostal(true)}
                    className="w-full text-xs text-muted-foreground hover:text-orange-500 py-2 flex items-center justify-center gap-1"
                    data-testid="geo-show-postal"
                  >
                    <Keyboard className="w-3 h-3" />
                    {fr ? 'Entrer mon code postal' : 'Enter my postal code'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
